import { collectGeneratedPdfEntryIds, type StepAudience } from '@bmo/form-core';

import { stepsWithPdfTemplates, type WorkflowTemplate } from '@/api/workflows';

import { localizedEntryLabel } from './entryLabel';
import { type FieldValue } from './FieldRenderer';
import { renderFilename } from './renderFilename';
import { visibleValues } from './visibleValues';

/**
 * Generates one filled PDF server-side.
 *
 * Two implementations, one per audience: `workflowsApi.generatePdfPreview` (authenticated —
 * advisor runtime and originator/approver preview) and
 * `publicWorkflowsApi.generatePdfPreview` (anonymous — the public customer runtime). The
 * request shape is identical because both endpoints share the same DTO server-side, which is
 * what lets one funnel serve both.
 */
export type PdfGenerator = (
  workflowId: string,
  body: {
    entryId: string;
    key: string;
    language: 'en' | 'fr';
    values: Record<string, unknown>;
  },
) => Promise<Blob>;

/**
 * One row on the PDF Generation step — one PDF the advisor will end up with.
 * Language-picked source key + a rendered filename for the download / tab
 * title. See `collectPdfDocs` below for how these are pulled from the
 * template.
 */
export type PdfDoc = {
  entryId: string;
  label: string;
  fileName: string;
  sourceStepTitle: string;
  /** S3 key for the language-picked template, passed to the preview endpoint to
   *  say WHICH document to generate. The browser never fetches it directly —
   *  since ADR-0031 the bytes are read server-side and only the filled result
   *  comes back. Null = originator never uploaded a file for this entry in
   *  either language, so there's nothing to generate. */
  previewKey: string | null;
  /** The language `previewKey`'s file is actually in — the requested one, or the other when the
   *  entry has a PDF in one language only. Generation must use THIS, not the application's
   *  language: field mappings and signature positions are authored per language against a
   *  specific file, so filling an English PDF as French puts values and signatures in the wrong
   *  boxes. Mirrors the server's `StepFileEntry.SourceFor`. */
  previewLanguage: 'en' | 'fr';
};

/** The file an application in this language is filled from, and that file's own language. */
function pickSource(
  enFileKey: string | null | undefined,
  frFileKey: string | null | undefined,
  isFr: boolean,
): { previewKey: string | null; previewLanguage: 'en' | 'fr' } {
  const [preferred, preferredKey, other, otherKey] = isFr
    ? (['fr', frFileKey, 'en', enFileKey] as const)
    : (['en', enFileKey, 'fr', frFileKey] as const);
  if (preferredKey) return { previewKey: preferredKey, previewLanguage: preferred };
  if (otherKey) return { previewKey: otherKey, previewLanguage: other };
  return { previewKey: null, previewLanguage: preferred };
}

/**
 * Flatten EVERY PDF source template across the workflow into a single list — including the
 * ones this application's answers have conditioned out. That is right for surfaces that describe
 * what the form CAN produce before any answers exist (the Welcome step's bullet list, the
 * Welcome config modal). Anything that means "the documents of THIS application" must call
 * {@link applicablePdfDocs} instead.
 *
 * Picks the current-language file key with a fallback to the other language
 * (matches the existing PdfGenerationStep behavior — a single-locale author
 * shouldn't leave an empty download slot).
 *
 * `language` also names the documents: a French application shows, and downloads, each PDF under
 * its French label when the originator authored one (QA D-31). Callers pass the client's PDF
 * language (`pdfLanguage`), the same one that picks the French file. The label here is for the
 * advisor only — the name BMO receives is minted server-side from the English label.
 */
export function collectPdfDocs(
  template: WorkflowTemplate,
  language: 'en' | 'fr',
  values: Record<string, FieldValue> = {},
): PdfDoc[] {
  const isFr = language === 'fr';
  return stepsWithPdfTemplates(template).flatMap((s) =>
    s.fileEntries.map<PdfDoc>((e) => {
      const label = localizedEntryLabel(e, language);
      // Filename template wins when authored — resolves `{field:<acroFieldName>}` +
      // `{today:<format>}` tokens against the current answers. Blank / unset template falls
      // back to the label so pre-templating workflows keep their filenames.
      //
      // Picked per language like the label on the line above, and for the same reason: without
      // a template a French application already downloads under `labelFr`, so an English-only
      // template is what takes the French away. An entry with no French template falls back to
      // the English one, which is what every workflow but RDSP relies on.
      const filenameTemplate = (isFr && e.filenameTemplateFr) || e.filenameTemplate;
      const fileName = renderFilename(
        filenameTemplate,
        label,
        values,
        template,
        'pdf',
        e.instanceScope,
      );
      return {
        entryId: e.id,
        label,
        fileName,
        sourceStepTitle: (isFr ? s.titleFr : s.titleEn) || s.titleEn || s.titleFr,
        ...pickSource(e.enFileKey, e.frFileKey, isFr),
      };
    }),
  );
}

/**
 * The PDFs THIS application produces: {@link collectPdfDocs} narrowed to the entries whose own
 * gating conditions pass against the current answers (QA D-11 — a Transfer form was listed,
 * downloaded and zipped for an application with no transfer).
 *
 * The rule is `collectGeneratedPdfEntryIds` from `@bmo/form-core`, deliberately not a second
 * evaluation here: that same list is what `buildESignSubmission` sends as
 * `generatedPdfEntryIds`, which decides the documents the server generates for BMO and which
 * signers still have something to sign. Sharing it keeps the PDF step, the download-all ZIP,
 * the review recap and the submitted package one resolution rather than lookalikes.
 * Instance-quantified (`instanceScope`) conditions are honoured because that function runs the
 * shared `evaluateConditions`.
 */
export function applicablePdfDocs(
  template: WorkflowTemplate,
  language: 'en' | 'fr',
  values: Record<string, FieldValue> = {},
): PdfDoc[] {
  const generated = new Set(collectGeneratedPdfEntryIds(template, values));
  return collectPdfDocs(template, language, values).filter((d) => generated.has(d.entryId));
}

/**
 * The "Download all" ZIP's file name.
 *
 * The workflow's `packageFilenameTemplate` when the originator authored one (the RDSP story's
 * `RDSP_<unique name>.zip`), rendered with the same tokens as a PDF's own filename and a `.zip`
 * extension. `defaultStem` — the bundle's first document's name, which is what the ZIP was
 * always called — is used when the template is unset, and also when it resolves to nothing.
 */
export function packageZipName(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  defaultStem: string,
): string {
  if (!template.packageFilenameTemplate?.trim()) return `${defaultStem}.zip`;
  return renderFilename(template.packageFilenameTemplate, defaultStem, values, template, 'zip');
}

/**
 * **The** values a PDF fill runs on, for every surface that fills one.
 *
 * The advisor's live answer map with everything their current answers have HIDDEN removed
 * (spec §6.4), plus whatever this `audience` never saw. `visibleValues` owns both rules; this
 * exists to give the result a name, because two surfaces now need the same map and they must
 * not resolve it separately.
 *
 * ## Why it is a function and not two call sites
 *
 * The preview path always sent this map. The final submit sent
 * `buildApplicationSnapshot(...).plctData` instead — the labelled snapshot BMO stores — and the
 * two are not the same thing. `visibleValues` is a DENYLIST: it drops keys it can prove are
 * hidden and passes everything else through, including keys it has never heard of. The snapshot
 * is an ALLOWLIST: it emits what its field walk knows how to emit, and a value that is not a
 * field is simply absent unless someone remembered to re-add it.
 *
 * That difference printed percentages into dollar boxes on a signed document. A dual-unit
 * column's "$ or %" mode is not a field — it lives at `unitMode:<fieldId>:<columnId>` — and
 * `collectFilledFields` filters each row handle on it, so with the key missing `readUnitMode`
 * fell back to the column's authored `defaultMode` (`dollar` everywhere), the `%Amount1`
 * handles filtered out and the `$Amount1` ones filled. Nothing failed; the package was just
 * wrong. Every future non-field key has the same shape.
 *
 * So the submitted package fills from THIS, exactly as the preview does. The snapshot still
 * travels on the same call as `fieldValues` — it is BMO's contract, labels and all — but it is
 * no longer what a PDF is built from.
 *
 * Pass the COMPOSED template a branched application actually walked, never the branch target
 * alone: the retained prefix's answers belong in documents that map them. See
 * {@link buildFilledPdfBlobUrl}'s `requestTemplateId` for the other half of that split.
 */
export function pdfFillValues(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  /** Who filled these answers in. Defaults to `'internal'` — the advisor runtime and both
   *  previews, where nothing is withheld on audience grounds. */
  audience: StepAudience = 'internal',
): Record<string, FieldValue> {
  return visibleValues(template, values, audience);
}

/**
 * Generate one filled PDF and hand back a Blob URL suitable for opening in a
 * new tab or triggering a download.
 *
 * **The fill happens server-side** (ADR-0021, ADR-0031). This used to download
 * the blank template, resolve the AcroForm mappings, stamp the e-signature
 * anchors, and run `pdf-lib` — all in the browser. `PdfFunction` does all of
 * that now, so no base PDF template and no fill logic ships to the client, and
 * the fill runs against the PUBLISHED template version the server loads rather
 * than whatever the client happens to be holding.
 *
 * The anchors are still stamped by the same single function that produces the
 * document, for the same reason as before: the advisor's preview, their
 * download, and the copy that reaches BMO must be one artifact. Tagging anywhere
 * else would mean a preview that differs from the document actually signed.
 *
 * Caller is responsible for `URL.revokeObjectURL` after the download / tab
 * open completes — leaking blob URLs across a long preview session keeps
 * every filled PDF pinned in memory.
 *
 * **Conditionally-hidden answers are pruned here** (spec §6.4) — by
 * {@link pdfFillValues}, the one funnel the final submit now shares — which is
 * why this takes the template and not just its id. Every preview and download in
 * the app routes through this one function, so gating here fixes all of them
 * and — unlike pruning at each call site — cannot drift out of step. The
 * server-side filler is no help: `collectFilledFields` walks every authored
 * field and reads no conditions, so a value orphaned by a branch change would
 * otherwise print into whatever box it is mapped to.
 *
 * Pruning client-side also keeps the payload smaller, which matters against
 * `PdfFunction`'s 6 MB synchronous-invoke ceiling, and follows the existing
 * split: gating conditions are evaluated in the browser and never reach the
 * BFF (spec §253), the same way `suppressedSignatures` already works.
 *
 * `audience` extends that same pruning to what the AUDIENCE never saw — an
 * `advisorOnly` step, and every advisor-sourced field — for the public customer
 * runtime. Same argument as the conditions: one funnel, so it cannot drift.
 */
export async function buildFilledPdfBlobUrl(
  template: WorkflowTemplate,
  key: string,
  values: Record<string, FieldValue>,
  /** The PDF template entry id being filled — scopes which field mappings
   *  apply, so a field mapped only to another PDF doesn't bleed in here. */
  entryId: string,
  /** Which language variant is being generated. Three things hang off this and
   *  they must agree: which of the entry's two files is filled, which signature
   *  placements are stamped, and which language-scoped `pdfMappings` apply. The
   *  server resolves all three from this one value. */
  language: 'en' | 'fr',
  /**
   * Which endpoint actually generates the bytes — the authenticated
   * `workflowsApi.generatePdfPreview`, or the anonymous
   * `publicWorkflowsApi.generatePdfPreview` for the public customer runtime.
   *
   * REQUIRED, and deliberately without a default. This module is the single funnel every
   * preview and download in the app passes through, so a default would be a silent
   * authenticated fallback: a public call site someone forgot to update would 401 in the
   * customer's face instead of failing to compile. Making it required is what forces every
   * existing call site to be visited when a new audience is added.
   */
  generate: PdfGenerator,
  /** Who filled these answers in. Defaults to `'internal'` — the advisor runtime
   *  and both previews, where nothing is withheld on audience grounds. */
  audience: StepAudience = 'internal',
  /**
   * Which workflow the fill is REQUESTED against. Defaults to `template.id`, which is right
   * for every form that has not branched.
   *
   * A branched form needs it to differ. `composeBranchedTemplate` pins the composed
   * template's `id` to the BASE form's on purpose — that id is what identifies the
   * application at BMO — but the PDF entries below the branch point belong to the TARGET.
   * The server validates the requested key against the steps of whatever workflow the URL
   * names and 404s anything it does not own (`PdfPreviewExecution.GenerateAsync`'s
   * `knownKeys` containment, which is also the control that stops a caller filling an
   * arbitrary S3 object — so it is not something to relax). Naming the target here is what
   * makes a branch's own documents fillable.
   *
   * Only the request target moves. `template` still supplies the entry lookup and the
   * visibility pruning below, because the COMPOSED form is what the user actually saw and
   * answered — pruning against the target's steps alone would drop the retained prefix's
   * answers from documents that map them.
   */
  requestTemplateId: string = template.id,
): Promise<string> {
  const blob = await generate(requestTemplateId, {
    entryId,
    key,
    language,
    values: pdfFillValues(template, values, audience),
  });
  return URL.createObjectURL(blob);
}

/**
 * Trigger a download via a temporary anchor. Revokes the object URL after
 * the click fires — the browser has by then already committed to the
 * download stream so revocation is safe.
 */
export function triggerBlobDownload(blobUrl: string, fileName: string) {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to bind the stream, then release.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
}
